import React from "react";
import { Pressable, Text, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";

import { SectionTitle } from "@/components/settings/SettingsPage";
import { colors, radius, text } from "@/components/settings/theme";
import { numberLocale, t, tc } from "@/localization";

// Pieces of the Event Analytics report: stat cards, the Viewers / Watch time
// switch and the breakdown lists with their bars. `dataSet={PDF_KEEP}` marks
// what the PDF never cuts in two (pdfPages.js); PDF_NEXT, a title, stays on
// the page of what follows it.

export const PDF_KEEP = { pdf: "keep" };
export const PDF_NEXT = { pdf: "next" };

export const formatNumber = (n) =>
  (Number(n) || 0).toLocaleString(numberLocale());

// 5100.5 hours -> "5100h 30m"
export const formatWatchTime = (hours) => {
  const minutes = Math.round((Number(hours) || 0) * 60);
  return t("{hours}h {minutes}m", {
    hours: Math.floor(minutes / 60),
    minutes: minutes % 60,
  });
};

export const StatCard = ({ title, value, subtitle }) => (
  <View
    dataSet={PDF_KEEP}
    style={[
      tw`p-4 mb-4 bg-white`,
      {
        borderWidth: 1,
        borderColor: colors.divider,
        borderRadius: radius.card,
      },
    ]}
  >
    <Text style={[text.hint, tw`mb-1`]}>{title}</Text>
    <Text style={[text.pageTitle, tw`mb-0.5`]} adjustsFontSizeToFit numberOfLines={1}>
      {value}
    </Text>
    {!!subtitle && <Text style={text.hint}>{subtitle}</Text>}
  </View>
);

// Two to four cards side by side on wide screens, two per row on phones.
export const StatRow = ({ children, wide }) => (
  <View style={tw`flex-row flex-wrap -mx-2`}>
    {React.Children.toArray(children)
      .filter(Boolean)
      .map((child, i) => (
        <View key={i} style={[tw`px-2`, { width: wide ? "25%" : "50%", minWidth: 150, flexGrow: 1 }]}>
          {child}
        </View>
      ))}
  </View>
);

export const MetricSwitch = ({ value, onChange, options }) => (
  <View
    accessibilityRole="tablist"
    style={[
      tw`flex-row mb-6 overflow-hidden`,
      { borderRadius: radius.control, borderWidth: 1, borderColor: colors.border },
    ]}
  >
    {options.map((o) => {
      const active = o.value === value;
      return (
        <Pressable
          key={o.value}
          accessibilityRole="tab"
          accessibilityState={{ selected: active }}
          onPress={() => onChange(o.value)}
          style={[
            tw`flex-1 py-2.5 px-3 flex-row items-center justify-center`,
            { backgroundColor: active ? colors.slate : colors.white },
          ]}
        >
          <Ionicons name={o.icon} size={18} color={active ? colors.white : colors.slate} style={tw`mr-2`} />
          <Text style={[text.label, { color: active ? colors.white : colors.slate }]}>{o.label}</Text>
        </Pressable>
      );
    })}
  </View>
);

// rows: [{ key, label, value, display }], largest first; bars scale to the
// largest. With `columns` ([{ label, width }], the PDF's), each row shows its
// `cells` under them instead of `display`. The PDF keeps a list on one page;
// only one taller than a page is cut, between rows.
const cell = (width) => [text.hint, { width, textAlign: "right", fontVariant: ["tabular-nums"] }];

export const BarList = ({ title, rows, note, columns }) => {
  if (!rows.length) return null;
  const max = Math.max(...rows.map((r) => r.value), 0) || 1;
  return (
    <View dataSet={PDF_KEEP} style={tw`mb-6`}>
      <View dataSet={PDF_NEXT}>
        <SectionTitle title={title} />
      </View>
      <View
        style={{
          borderWidth: 1,
          borderColor: colors.divider,
          borderRadius: radius.card,
          overflow: "hidden",
        }}
      >
        {!!columns && (
          <View dataSet={PDF_NEXT} style={tw`px-4 py-2 flex-row items-center`}>
            <View style={tw`flex-1`} />
            {columns.map((c) => (
              <Text key={c.label} style={[cell(c.width), tw`font-semibold`]}>
                {c.label}
              </Text>
            ))}
          </View>
        )}
        {rows.map((r, i) => (
          <View
            key={r.key}
            dataSet={PDF_KEEP}
            style={[
              tw`px-4 flex-row items-center justify-between`,
              columns ? tw`py-2` : tw`py-3`,
              (i > 0 || columns) && { borderTopWidth: 1, borderTopColor: colors.divider },
            ]}
          >
            <View
              accessible={false}
              style={[
                tw`absolute top-0 bottom-0 left-0`,
                { width: `${(r.value / max) * 100}%`, backgroundColor: colors.infoTint },
              ]}
            />
            <Text style={[text.body, tw`flex-1 pr-3`]} numberOfLines={1}>
              {r.label}
            </Text>
            {columns ? (
              columns.map((c, j) => (
                <Text key={c.label} style={cell(c.width)}>
                  {r.cells[j]}
                </Text>
              ))
            ) : (
              <Text style={text.hint}>{r.display}</Text>
            )}
          </View>
        ))}
      </View>
      {!!note && (
        <Text dataSet={PDF_KEEP} style={[text.hint, tw`mt-2`]}>
          {note}
        </Text>
      )}
    </View>
  );
};

export const unknownLabel = () => tc("analytics", "Unknown");
export const otherLabel = () => tc("analytics", "Other");
